/**
 * INCENTIVE-TRACKER MODULE
 * Self-contained. Import only from /shared or within this module.
 *
 * PROGRESS (backlog items 231 + 232)
 *
 * One place that answers "how is this program doing right now", used by three
 * surfaces so they can never disagree:
 *   · ProgressPanel      — live progress while a program is sent/active
 *   · ScoringPanel       — pre-fills each goal's actual at scoring time
 *   · DistributorProgressPage — the read-only per-distributor view
 *
 * A goal that already has a captured score always reports THAT number, not a
 * freshly resolved one: incentive_scores is an immutable record, and a screen
 * that quietly restates history is worse than one that shows nothing.
 */

import { resolveActual, RESOLVER_RESULT } from "./metricResolver.js";
import { computePayout, attainmentPct, num } from "./payout.js";

/**
 * Progress for one goal.
 * @returns {Promise<{
 *   goal: object, actual: number|null, hasActual: boolean, pct: number,
 *   payout: number|null, source: 'score'|'system'|'none',
 *   basis: string, reason: string, scored: boolean
 * }>}
 */
export async function goalProgress({ program, goal, score, accountId }) {
  if (score) {
    const actual = num(score.actual_value);
    return {
      goal,
      actual,
      hasActual: true,
      pct: attainmentPct(actual, goal.target_value),
      payout: score.payout_earned == null ? null : num(score.payout_earned),
      source: "score",
      basis: score.resolved_source === "system"
        ? "Captured from your depletion reports at scoring"
        : "Entered at scoring",
      reason: "",
      scored: true,
    };
  }

  const res = await resolveActual({
    metricType: goal.metric_type,
    startDate: program.start_date,
    endDate: program.end_date,
    distributor: program.distributor_name,
    targetAccounts: goal.target_accounts,
    targetProducts: goal.target_products,
    accountId,
  });

  if (res.status !== RESOLVER_RESULT.RESOLVED) {
    return {
      goal, actual: null, hasActual: false, pct: 0, payout: null,
      source: "none", basis: "", reason: res.reason, scored: false,
    };
  }

  const actual = num(res.value);
  const computed = computePayout(goal, actual);
  return {
    goal,
    actual,
    hasActual: true,
    pct: attainmentPct(actual, goal.target_value),
    payout: computed,
    source: "system",
    basis: res.basis || "From your depletion reports",
    reason: "",
    scored: false,
  };
}

/**
 * Progress for every goal in a program. Resolves in parallel — each goal is one
 * or two RPC calls and programs carry a handful of goals, not hundreds.
 */
export async function programProgress({ program, goals = [], scores = [], accountId }) {
  const byGoal = new Map(scores.map((s) => [s.goal_id, s]));
  const items = await Promise.all(
    goals.map((goal) => goalProgress({ program, goal, score: byGoal.get(goal.id), accountId })),
  );
  return { program, items, ...summarize(items) };
}

/**
 * Roll a set of goal-progress items into the headline numbers. Pure — unit
 * tested. `pct` is the average attainment across goals that have a real target
 * (a custom / target-less goal has no meaningful percentage and is excluded
 * rather than counted as 0%, which would drag the headline down for free).
 */
export function summarize(items = []) {
  const withTarget = items.filter((i) => num(i.goal?.target_value) > 0);
  const measured = withTarget.filter((i) => i.hasActual);
  const pct = measured.length === 0
    ? 0
    : measured.reduce((s, i) => s + Math.min(i.pct, 200), 0) / measured.length;
  return {
    goalCount: items.length,
    measuredCount: measured.length,
    unmeasuredCount: items.length - measured.length,
    goalsMet: withTarget.filter((i) => i.hasActual && i.pct >= 100).length,
    targetedCount: withTarget.length,
    pct,
    payout: items.reduce((s, i) => s + num(i.payout), 0),
    anyLive: items.some((i) => i.source === "system"),
    allScored: items.length > 0 && items.every((i) => i.scored),
  };
}

/** Bar width for an attainment percentage — capped so 300% doesn't overflow. */
export function barWidth(pct) {
  return `${Math.max(0, Math.min(100, num(pct)))}%`;
}
